import * as React from 'react'
import { ColoredSpan } from '../../../../common/src/components/core'
import { Chars } from '../../../../common/src/constants'
import { isBetween, diffMins } from '../../../../common/src/date'
import Time from '../../../../common/src/time'
import { FetchAllUsersQueryQuery, FetchUserContextQuery } from '../../../../common/src/types.gen'
import { Status, StatusString } from './StatusString'
export function dash() {
  return <ColoredSpan color="steel">{Chars.mDash}</ColoredSpan>
}

interface UserStatusProps {
  user: FetchAllUsersQueryQuery['allUsers'][0] | FetchUserContextQuery['self']
}

/**
 * Get the user {@link Status}.
 */
export function UserStatus(props: UserStatusProps) {
  const { user } = props
  const { isClockedIn, activeOperatorShift } = user

  // no shift today
  if (activeOperatorShift == null) {
    return isClockedIn ? <StatusString status={Status.PROJECT} /> : dash()
  }

  const now = Time.now()
  const shiftStart = new Date(activeOperatorShift.shift.timeStart)
  const shiftEnd = new Date(activeOperatorShift.shift.timeEnd)

  const hasShiftNow = isBetween(shiftStart, now, shiftEnd)

  return isClockedIn ? clockedInStatus(user, hasShiftNow) : clockedOutStatus(user, hasShiftNow)
}

/**
 * Render either the station name or {@link Status#PROJECT} given that the user is clocked in.
 */
function clockedInStatus(
  user: FetchAllUsersQueryQuery['allUsers'][0] | FetchUserContextQuery['self'],
  hasShiftNow: boolean
) {
  const { lookingAtUser, autoStationAssignmentEnabled } = user

  if (hasShiftNow && autoStationAssignmentEnabled) {
    return lookingAtUser == null ? (
      <StatusString status={Status.VIEWING_ALL} />
    ) : lookingAtUser.id === user.id ? (
      <StatusString status={Status.CLOCKED_IN} />
    ) : (
      <StatusString status={Status.VIEWING} />
    )
  }

  return <StatusString status={Status.PROJECT} />
}

/**
 * Render either {@link Status#ON_BREAK} or {@link Status#MISSING} given that the user is clocked out.
 */
function clockedOutStatus(
  user: FetchAllUsersQueryQuery['allUsers'][0] | FetchUserContextQuery['self'],
  hasShiftNow: boolean
) {
  const { mostRecentClockEvent } = user
  const now = Time.now()

  if (!hasShiftNow) {
    return dash()
  }

  // never clocked in
  if (mostRecentClockEvent == null) {
    return <StatusString status={Status.MISSING} />
  } else {
    const { clockOutTime } = mostRecentClockEvent

    // impossible because user is clocked out
    if (clockOutTime == null) {
      return dash()
    }

    const mostRecentClockOutTime = new Date(clockOutTime)

    // Operators generally take 10 minutes break. If they don't come back in 15 minutes,
    // we consider them as missing.
    const maxBreakDurationInMinutes = 15

    return diffMins(now, mostRecentClockOutTime) < maxBreakDurationInMinutes ? (
      <StatusString status={Status.ON_BREAK} />
    ) : (
      <StatusString status={Status.MISSING} />
    )
  }
}
